import { type ChangeEvent, createRef, type CSSProperties, type MouseEvent, PureComponent } from 'react';
import {
    extractSuggestionLocationParameters,
    getBaseLocationParams,
    getEmptyLocationParams,
    getLocationText,
    makeSuggestionName,
} from './locationUtils.js';
import './external-adapters/typeaheadJqueryAdapter.js';
import { locationBloodhound } from './locationBloodhound.js';
import {
    getCityName,
    getCountyName,
    getLatLng,
    getStateCode,
    getZip,
    isCity,
    isCounty,
    isState,
} from './placesUtils.js';
import { isValidZip } from '../utils/addressUtils.ts';
import type { SearchCallbacks } from '../search/types/searchPage';
import { LocationSuggestion } from '../search/types/typeaheadSuggestions';
import { listenForOutsideClicks } from '../utils/react/onClickOutside';

// From Google Maps library
/* global google */

interface LocationSearchBoxProps {
    searchURL: string;
    callbacks: SearchCallbacks;
    detectedLocation?: object;
    projects?: {
        breadcrumb?: [string, string][];
    };
    registerSearchCallback?: () => void;
    searchAll?: () => void;
    makeKeywordsURL?: () => string;
    directlyUpdateText?: boolean;
}

interface LocationSearchBoxState {
    keywords: string;
    isOpen: boolean;
}

// TODO: Refactor to separate reusable typeahead search from search-page-specific stuff?
export class LocationSearchBox extends PureComponent<LocationSearchBoxProps, LocationSearchBoxState> {
    // This isn't written in TS and it's deprecated, so just using any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private locationBloodhound: any = null;
    private readonly inputRef = createRef<HTMLInputElement>();
    private readonly wrapperRef = createRef<HTMLSpanElement>();
    private placesService: google.maps.places.PlacesService | undefined;
    private selectedSuggestion: LocationSuggestion | null = null;
    private placeDetailFields = ['address_component', 'adr_address', 'alt_id', 'formatted_address', 'geometry', 'icon', 'name', 'photo', 'place_id', 'plus_code', 'scope', 'type', 'url', 'vicinity'];
    private removeClickListener: (() => void) | undefined;

    state: LocationSearchBoxState = {
        keywords: getLocationText(this.props.searchURL),
        isOpen: false,
    };

    componentDidMount() {
        this.removeClickListener = listenForOutsideClicks(this.wrapperRef, this.close);

        this.locationBloodhound = locationBloodhound.make();
        const $input = $(this.inputRef.current!);

        $input.typeahead<LocationSuggestion>({
            highlight: true,
            hint: false,
        }, {
            name: 'locations',
            source: this.locationBloodhound.ttAdapter(),
            display: 'name',
            templates: {
                suggestion: function (location: { name: string }) {
                    return "<div class='suggestion location-suggestion'>" + location.name + '</div>';
                },
                notFound: "<div class='no-suggestions'>No suggestions</div><a class='suggestion location-suggestion tt-suggestion tt-selectable map-suggestion' href='/donors/map.html'>View map of local classroom projects</a>",
                footer: "<a class='suggestion location-suggestion tt-suggestion tt-selectable map-suggestion' href='/donors/map.html'>View map of local classroom projects</a><div class='powered-by'>powered by<img src='" + baseCacheURL +
                        "images/powered-by-google/google_on_white_hdpi.png'" +
                        " alt='Powered by Google' /></div>",
            },
        }).on('typeahead:selected', (_event, suggestion) => {
            this.selectedSuggestion = suggestion;
            this.search();
        }).keypress(e => {
            if (e.keyCode === 13) {
                this.search();
            }
        });

        // this is a hack, but React is turning autoComplete="new-password" into autocomplete="off" (off is no longer recognized by most browsers)
        $input.attr('autocomplete', 'new-password');
    }

    componentWillUnmount() {
        this.removeClickListener?.();

        if (this.inputRef.current) {
            $(this.inputRef.current).typeahead('destroy');
        }
    }

    componentDidUpdate() {
        if (this.props.callbacks.setAllowLocationSearchSubmit) {
            this.updateHasInput();
        }
    }

    UNSAFE_componentWillReceiveProps(newProps: LocationSearchBoxProps) {
        const keywords = getLocationText(newProps.searchURL);
        if (keywords !== getLocationText(this.props.searchURL)) {
            this.setState({
                keywords,
            });
        }
        $(this.inputRef.current!).typeahead('close');
        this.close();
    }

    toggleOpen = (open?: boolean) => {
        this.setState({
            isOpen: (typeof open === 'boolean') ? open : !this.state.isOpen,
        });
    };

    close = () => {
        this.setState({ isOpen: false });
    };

    updateHasInput = () => {
        const hasInput = this.state.keywords.length > 0;
        this.props.callbacks.setAllowLocationSearchSubmit?.(hasInput);
    };

    search = () => {
        (this.props.searchAll || this.submit)();
    };

    /**
     * Searches for the selected location
     * @param searchURL The search URL to use (it may have had keywords added, meaning it wouldn't match props.searchURL)
     */
    submit = (searchURL?: string) => {
        $(this.inputRef.current!).blur();
        const currentSearchURL = searchURL || this.props.searchURL;

        if (this.selectedSuggestion) {
            this.setLocation(currentSearchURL, this.selectedSuggestion);
            this.selectedSuggestion = null;
            return;
        }

        const callbacks = this.props.callbacks;
        const keywords = this.state.keywords;

        if (keywords === getLocationText(this.props.searchURL)) {
            callbacks.changeURL(currentSearchURL);
            return;
        }
        if (keywords === '') {
            const newURL = callbacks.makeSearchURL(getEmptyLocationParams(), currentSearchURL);
            callbacks.changeURL(newURL);
        } else {
            this.locationBloodhound.search(keywords, (locations: LocationSuggestion[]) => {
                // Use the first available suggestion, if it exists
                if (locations && locations.length > 0) {
                    this.setLocation(currentSearchURL, locations[0]);
                } else {
                    callbacks.changeURL(currentSearchURL);
                }
            });
        }
    };

    setLocation = (searchURL: string, prediction: { name: string; place_id: string }) => {
        if (!this.placesService) {
            this.placesService = new google.maps.places.PlacesService(document.createElement('div'));
        }

        const name = prediction.name;

        if (this.props.directlyUpdateText && name) {
            this.setState({
                keywords: name,
            });
        }

        this.placesService.getDetails({
            placeId: prediction.place_id,
            fields: this.placeDetailFields,
        }, function (this: LocationSearchBox, place: google.maps.places.PlaceResult | null) {
            if (!place) return;
            const params = getBaseLocationParams();

            if (isState(place)) {
                params.state = getStateCode(place);
                params.includeNearbyLocations = null;
            } else if (isCity(place)) {
                params.state = getStateCode(place);
                params.cityName = getCityName(place);
                params.countyName = getCountyName(place);
                const latLng = getLatLng(place);
                params.centerLat = latLng.lat;
                params.centerLng = latLng.lng;
            } else if (isCounty(place)) {
                params.state = getStateCode(place);
                params.countyName = getCountyName(place);
                const latLng = getLatLng(place);
                params.centerLat = latLng.lat;
                params.centerLng = latLng.lng;
            } else { // zip
                const latLng = getLatLng(place);
                params.centerLat = latLng.lat;
                params.centerLng = latLng.lng;
                params.centerZip = getZip(place);
                params.locationName = name;
            }

            this.close();
            const newURL = this.props.callbacks.makeSearchURL(params, searchURL);
            this.props.callbacks.changeURL(newURL);
        }.bind(this));

        window.dcAnalytics.track('Location Selected', { name });
    };

    handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({
            keywords: event.target.value.replace(/^\s+/, ''),
        }, this.handleDefaultSuggestions);
    };

    handleDefaultSuggestions = () => {
        this.toggleOpen(this.state.keywords === '');
    };

    selectDefaultLocation = (event: MouseEvent<HTMLAnchorElement>) => {
        this.close();

        const props = this.props;
        const url = props.makeKeywordsURL ? props.makeKeywordsURL() : props.searchURL;
        const newURL = this.makeSuggestionURL(url);

        this.props.callbacks.changeURL(newURL);

        event.preventDefault();
    };

    /**
     * @param searchURL Override search URL
     * @return Search URL for the detected location
     */
    makeSuggestionURL(searchURL?: string) {
        const props = this.props;
        const params = extractSuggestionLocationParameters(props.detectedLocation);
        return props.callbacks.makeSearchURL(params, searchURL);
    }

    render() {
        const props = this.props;
        let hiddenStyle;

        if (props.projects && props.projects.breadcrumb) {
            // We let the legacy zip sort hide the new location selector
            const isLocationSort = props.projects.breadcrumb.some(function (crumb) {
                return crumb[0] === 'keywords' && isValidZip(crumb[1]);
            });
            if (isLocationSort) {
                hiddenStyle = {
                    display: 'none',
                };
            }
        }

        let displaySuggestions: CSSProperties = {
            display: 'none',
        };

        let suggestionName, suggestionURL;
        if (props.detectedLocation && this.state.keywords === '') {
            suggestionName = makeSuggestionName(props.detectedLocation);
            suggestionURL = this.makeSuggestionURL();

            if (this.state.isOpen) {
                displaySuggestions = {
                    display: 'block',
                };
            }
        }

        return (
            <span style={hiddenStyle} ref={this.wrapperRef}>
                <span className="search-string">near</span>
                <span className="location-holder">
                    <input className="location-search" type="text" name="location"
                        placeholder="city, state, or zip"
                        ref={this.inputRef}
                        value={this.state.keywords} onChange={this.handleChange}
                        onClick={this.handleDefaultSuggestions}
                        onFocus={this.handleDefaultSuggestions}
                        aria-label="Location search: suggestions appear below" />
                    <div className="subnav location-geosuggestion" style={displaySuggestions}>
                        <ul>
                            <li>
                                <a href={suggestionURL} onClick={this.selectDefaultLocation}>
                                    <span className="suggestion-label">Find classroom projects near you</span>
                                    {suggestionName}
                                </a>
                            </li>
                            <li>
                                <a href={`${baseURL}donors/map.html`}>
                                    <span className="suggestion-label">Explore your local area</span>
                                    View map
                                </a>
                            </li>
                        </ul>
                    </div>
                </span>
            </span>
        );
    }
}
